import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import SublyApi from '../../helpers/Api';

const NotificationSettings = () => {
	const token = useSelector((state) => state.admin.token);
	const [emails, setEmails] = useState(['borhlaw@icloud.com']);
	const [newEmail, setNewEmail] = useState('');
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [message, setMessage] = useState(null);
	const [error, setError] = useState(null);

	// Load current settings on component mount
	useEffect(() => {
		if (token) {
			loadSettings();
		} else {
			setError('Admin authentication required');
			setLoading(false);
		}
	}, [token]);

	const loadSettings = async () => {
		if (!token) {
			setError('Admin authentication required');
			return;
		}

		setLoading(true);
		try {
			const response = await SublyApi.request(
				'api/admin/settings',
				{},
				'get',
				{ Authorization: `Bearer ${token}` }
			);
			setEmails(
				response.settings.notification_emails || ['borhlaw@icloud.com']
			);
		} catch (err) {
			console.error('Failed to load settings:', err);
			setError('Failed to load notification settings');
		} finally {
			setLoading(false);
		}
	};

	const saveSettings = async () => {
		if (!token) {
			setError('Admin authentication required');
			return;
		}

		setSaving(true);
		setMessage(null);
		setError(null);

		try {
			const response = await SublyApi.request(
				'api/admin/settings',
				{ notification_emails: emails },
				'put',
				{ Authorization: `Bearer ${token}` }
			);

			setMessage('Notification settings saved successfully!');
			setEmails(response.settings.notification_emails);

			// Clear success message after 3 seconds
			setTimeout(() => setMessage(null), 3000);
		} catch (err) {
			console.error('Failed to save settings:', err);
			const errorMsg = Array.isArray(err) ? err[0] : (err || 'Failed to save settings');
			setError(errorMsg);
		} finally {
			setSaving(false);
		}
	};

	const addEmail = () => {
		if (!newEmail.trim()) return;

		// Basic email validation
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(newEmail)) {
			setError('Please enter a valid email address');
			return;
		}

		if (emails.includes(newEmail)) {
			setError('This email is already in the list');
			return;
		}

		setEmails([...emails, newEmail]);
		setNewEmail('');
		setError(null);
	};

	const removeEmail = (emailToRemove) => {
		if (emails.length <= 1) {
			setError('At least one notification email is required');
			return;
		}
		setEmails(emails.filter((email) => email !== emailToRemove));
		setError(null);
	};

	const handleKeyPress = (e) => {
		if (e.key === 'Enter') {
			addEmail();
		}
	};

	if (loading) {
		return (
			<div className='flex items-center justify-center p-8'>
				<div className='text-gray-600'>Loading notification settings...</div>
			</div>
		);
	}

	return (
		<div className='max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md'>
			<div className='mb-6'>
				<h2 className='text-2xl font-bold text-gray-800 mb-2'>
					📧 Email Notification Settings
				</h2>
				<p className='text-gray-600'>
					Manage email addresses that receive notifications when new form
					submissions are created.
				</p>
			</div>

			{/* Current Email List */}
			<div className='mb-6'>
				<h3 className='text-lg font-semibold text-gray-700 mb-3'>
					Current Notification Emails
				</h3>
				<div className='space-y-2'>
					{emails.map((email, index) => (
						<div
							key={index}
							className='flex items-center justify-between p-3 bg-gray-50 rounded-md border'>
							<div className='flex items-center'>
								<span className='text-green-600 mr-2'>✉️</span>
								<span className='text-gray-800 font-medium'>{email}</span>
							</div>
							<button
								onClick={() => removeEmail(email)}
								disabled={emails.length <= 1}
								className={`px-3 py-1 rounded text-sm font-medium ${
									emails.length <= 1
										? 'bg-gray-200 text-gray-400 cursor-not-allowed'
										: 'bg-red-100 text-red-600 hover:bg-red-200'
								} transition-colors`}
								title={
									emails.length <= 1
										? 'At least one email is required'
										: 'Remove email'
								}>
								Remove
							</button>
						</div>
					))}
				</div>
			</div>

			{/* Add New Email */}
			<div className='mb-6'>
				<h3 className='text-lg font-semibold text-gray-700 mb-3'>
					Add New Email
				</h3>
				<div className='flex gap-2'>
					<input
						type='email'
						value={newEmail}
						onChange={(e) => setNewEmail(e.target.value)}
						onKeyPress={handleKeyPress}
						placeholder='Enter email address'
						className='flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
					/>
					<button
						onClick={addEmail}
						disabled={!newEmail.trim()}
						className={`px-4 py-2 rounded-md font-medium ${
							!newEmail.trim()
								? 'bg-gray-300 text-gray-500 cursor-not-allowed'
								: 'bg-blue-600 text-white hover:bg-blue-700'
						} transition-colors`}>
						Add Email
					</button>
				</div>
			</div>

			{/* Save Button */}
			<div className='mb-6'>
				<button
					onClick={saveSettings}
					disabled={saving || emails.length === 0}
					className={`w-full py-3 px-4 rounded-md font-medium ${
						saving || emails.length === 0
							? 'bg-gray-400 cursor-not-allowed'
							: 'bg-green-600 hover:bg-green-700'
					} text-white transition-colors`}>
					{saving ? 'Saving...' : 'Save Notification Settings'}
				</button>
			</div>

			{/* Success Message */}
			{message && (
				<div className='mb-4 p-4 bg-green-50 border border-green-200 rounded-md'>
					<div className='flex items-center'>
						<span className='text-green-600 mr-2'>✅</span>
						<span className='text-green-800 font-medium'>{message}</span>
					</div>
				</div>
			)}

			{/* Error Message */}
			{error && (
				<div className='mb-4 p-4 bg-red-50 border border-red-200 rounded-md'>
					<div className='flex items-center'>
						<span className='text-red-600 mr-2'>❌</span>
						<span className='text-red-800 font-medium'>{error}</span>
					</div>
				</div>
			)}

			{/* Info Box */}
			<div className='p-4 bg-blue-50 border border-blue-200 rounded-md'>
				<h4 className='text-blue-800 font-medium text-sm mb-2'>
					ℹ️ How it works:
				</h4>
				<ul className='text-blue-700 text-sm space-y-1'>
					<li>
						• All listed emails will receive notifications when users submit
						forms
					</li>
					<li>• Emails are sent immediately after form submission</li>
					<li>
						• Each email contains a direct link to view the submission details
					</li>
					<li>• At least one email address is required</li>
				</ul>
			</div>
		</div>
	);
};

export default NotificationSettings;
